/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import React, { ChangeEvent } from 'react';
import {
  Controller,
  Control,
  FieldError,
  FieldPath,
  FieldValues,
  PathValue,
  Path,
} from 'react-hook-form';
import { Link } from 'react-router-dom';

import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';

type Props<T extends FieldValues> = {
  options: PathValue<T, Path<T>>[];
  control: Control<T, object>;
  name: FieldPath<T>;
  label: string;
  isRequired?: boolean;
  error?: FieldError | undefined;
  required?: boolean | undefined;
  helperText?: string | undefined;
  link?: string | undefined;
  onChange?: (value: string) => void;
};

export const InputSelector = <T extends FieldValues>({
  name,
  label,
  options,
  isRequired,
  control,
  error,
  required,
  helperText,
  link,
  onChange,
}: Props<T>) => {
  const handleOnChange = (
    event: ChangeEvent<{ name?: string | undefined; value: unknown }>,
    controlledFieldOnChange: (
      event: PathValue<T, Path<T>> | ChangeEvent<Element>,
    ) => void,
  ) => {
    if (event.target.name) {
      controlledFieldOnChange(event.target.value as PathValue<T, Path<T>>);
      if (onChange) {
        onChange(event.target.value as string);
      }
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={options[0]}
      rules={{
        required: required,
        validate: value => {
          return !value?.includes('Select');
        },
      }}
      render={({ field }) => (
        <FormControl fullWidth>
          <InputLabel
            required={isRequired === undefined ? false : isRequired}
            htmlFor="select-outlined"
            id="select-outlined-label"
            style={{
              marginTop: '0.25rem',
            }}
          >
            {label}
          </InputLabel>
          <Select
            {...field}
            onChange={e => handleOnChange(e, field.onChange)}
            labelId="select-outlined-label"
            id="select-outlined"
            data-testid={label}
            label={label}
            error={!!error}
          >
            {options.map(option => {
              return (
                <MenuItem button key={option} value={option}>
                  {option}
                </MenuItem>
              );
            })}
          </Select>
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
          {link && (
            <Link
              style={{
                color: '#004b7f',
                marginTop: '4px',
                textDecorationLine: 'underline',
                fontSize: '10px',
              }}
              to={link}
            >
              Read more about each Focus Area
            </Link>
          )}
        </FormControl>
      )}
    />
  );
};
